import { toast } from 'react-toastify';

const isBlank = v => typeof v !== 'string' || v.trim() === '';

const isSentinelDate = d => {
  if (!d) return true;
  // Handles Date, ISO string, or yyyy-mm-dd...
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  return s.startsWith('1900-01-01');
};

const isValidTime = t => {
  if (!t && t !== '0') return false;
  if (t === '') return false;
  const m = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(t);
  return !!m;
};

const isSentinelTime = t => !isValidTime(t);

const safeTrim = v => (typeof v === 'string' ? v.trim() : (v ?? ''));

const getVal = obj => {
  if (obj && typeof obj === 'object') {
    return safeTrim(obj.value);
  }
  return safeTrim(obj);
};

const isValidQuote = quote => {
  const fromCity = getVal(quote?.fromCity);
  const toCountry = getVal(quote?.toCountry);
  const toCity = getVal(quote?.toCity);
  const date = quote?.date;
  const deptTime = quote?.deptTime;
  const airline = getVal(quote?.airline);

  if (isBlank(fromCity)) return false;
  if (isBlank(toCountry)) return false;
  if (isBlank(toCity)) return false;
  if (!date || isSentinelDate(date)) return false;
  if (isSentinelTime(deptTime)) return false;
  if (isBlank(airline)) return false;

  return true;
};

// Option is valid if it has required option-level fields AND >=1 valid quote
const isValidOption = opt => {
  if (isBlank(opt?.agency)) return false;
  if (isBlank(opt?.currency)) return false;

  const qts = Array.isArray(opt?.quotes) ? opt.quotes : [];
  const validQuotes = qts.filter(isValidQuote);
  return validQuotes.length > 0;
};

const sanitiseFlightOptions = (flightOptions = []) => {
  const clean = [];

  flightOptions.forEach(opt => {
    // keep only valid quotes
    const quotes = (opt?.quotes || []).filter(isValidQuote);
    const candidate = { ...opt, quotes };

    if (isValidOption(candidate)) {
      clean.push(candidate);
    }
  });

  return clean;
};

// ---- HOTEL: validators ----
const isSentinelYmd = s => typeof s === 'string' && s.startsWith('1900-01-01');

const isValidHotelQuote = q => {
  const country = getVal(q?.country); // object {value,label} or string
  const place = safeTrim(q?.place);
  const inDate = q?.checkInDate;
  const outDate = q?.checkOutDate;

  if (isBlank(country)) {
    return false;
  }
  if (isBlank(place)) {
    return false;
  }

  if (!inDate || isSentinelDate(inDate) || isSentinelYmd(inDate)) {
    return false;
  }
  if (!outDate || isSentinelDate(outDate) || isSentinelYmd(outDate)) {
    return false;
  }

  if (new Date(outDate) < new Date(inDate)) {
    return false;
  }

  return true;
};

const isValidHotelOption = opt => {
  if (isBlank(opt?.agency)) return false;
  if (isBlank(opt?.currency)) return false;

  const quotes = Array.isArray(opt?.quotes)
    ? opt.quotes.filter(isValidHotelQuote)
    : [];
  return quotes.length > 0;
};

const sanitiseHotelOptions = (hotelOptions = []) =>
  hotelOptions.reduce((acc, opt) => {
    const quotes = (opt?.quotes || []).filter(isValidHotelQuote);
    const candidate = { ...opt, quotes };
    if (isValidHotelOption(candidate)) acc.push(candidate);
    return acc;
  }, []);

export const mapFlightOptionsToRequestBody = (rawFlightOptions = []) => {
  const header = {};
  const flightOptionList = [];

  const flightOptions = sanitiseFlightOptions(rawFlightOptions);

  const toApiTime = t => {
    if (!t) return '';
    return t.length === 5 ? `${t}:00` : t;
  };

  flightOptions.forEach((option, optionIndex) => {
    const optNo = optionIndex + 1;

    header[`oP${optNo}TravelAgency`] = option.agency || '';
    header[`fOP${optNo}Currency`] = option.currency || '';
    header[`fOP${optNo}QuoteValue`] = Number(option.quoteValue) || 0;
    header[`oP${optNo}Refund`] = Number(option.refund) || 0;
    header[`oP${optNo}Rebooking`] = option.rebooking || '';
    header[`fOP${optNo}Terms`] = option.terms || '';

    option.quotes?.forEach((quote, lineIndex) => {
      const fromCity = getVal(quote?.fromCity);
      const toCountry = getVal(quote?.toCountry);
      const toCity = getVal(quote?.toCity);
      const airline = getVal(quote?.airline);

      flightOptionList.push({
        optNo,
        lNo: lineIndex + 1,
        flightFrom: fromCity,
        flightToCountry: toCountry,
        flightTo: toCity,
        flightDate: quote?.date === '' ? '' : quote?.date || '',
        flightDeptTime: toApiTime(quote?.deptTime),
        flightAirLine: airline,
        flightClass: quote?.class || '',
        travelAgency: option.agency || '',
        currency: option.currency || '',
        quoteValue: Number(option.quoteValue) || 0,
        refund: Number(option.refund) || 0,
        rebooking: option.rebooking || '',
        terms: option.terms || '',
        clientFlight: quote?.traveller || '',
      });
    });
  });

  return { header, flightOptionList };
};

export const mapHotelOptionsToRequestBody = (rawHotelOptions = []) => {
  const header = {};
  const hotelOptionList = [];

  const hotelOptions = sanitiseHotelOptions(rawHotelOptions);

  hotelOptions.forEach((option, optionIndex) => {
    const optNo = optionIndex + 1;

    header[`hOP${optNo}Currency`] = option.currency;
    header[`oP${optNo}RatePerNight`] = Number(option.ratesPerNight);
    header[`hOP${optNo}QuoteValue`] = Number(option.quoteValue);
    header[`hOP${optNo}Terms`] = option.terms;
    header[`hOP${optNo}TravelAgency`] = option.agency;

    option.quotes.forEach((quote, lineIndex) => {
      const countryVal = getVal(quote.country);
      const countryLabel =
        typeof quote.country === 'object'
          ? safeTrim(quote.country?.label)
          : safeTrim(quote.country);

      hotelOptionList.push({
        optNo,
        lNo: lineIndex + 1,
        hotelCountry: countryVal,
        hotelCountryName: countryLabel,
        hotelPlace: quote.place,
        hotelChkIn: isSentinelYmd(quote.checkInDate)
          ? ''
          : quote.checkInDate || '',
        hotelChkOut: isSentinelYmd(quote.checkOutDate)
          ? ''
          : quote.checkOutDate || '',
        hotelName: quote.hotelName,
        currency: option.currency,
        ratePerNight: Number(option.ratesPerNight),
        quoteValue: Number(option.quoteValue),
        terms: option.terms,
        travelAgencyHotel: option.agency,
        clientHotel: quote.traveller,
      });
    });
  });

  return { header, hotelOptionList };
};

export const isOptionFilled = (optionList, optNo) => {
  if (!Array.isArray(optionList)) return false;

  const segments = optionList.filter(item => item.optNo === optNo);
  return segments.some(segment =>
    Object.values(segment).some(
      val =>
        val !== '' &&
        val !== null &&
        val !== undefined &&
        !(typeof val === 'string' && val.trim() === ''),
    ),
  );
};

export const getTrimmedString = value => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return String(value.value).trim();
  }
  return String(value || '').trim();
};

export const validateComponent = ({
  label,
  cost,
  quote,
  status,
  agency,
  comments,
  opt,
}) => {
  const msg = opt === 'C' ? 'refund' : 'rebook';
  const costValue = parseInt(cost || 0, 10);
  const quoteValue = parseInt(quote || 0, 10);

  if (
    cost == null ||
    cost === '' ||
    Number.isNaN(costValue) ||
    (opt !== 'C' && costValue <= 0)
  ) {
    return {
      isValid: false,
      message: `Please enter ${msg} cost for ${label}`,
      field: `${label.toLowerCase()}Cost`,
    };
  }

  if (opt === 'C' && costValue > quoteValue) {
    return {
      isValid: false,
      message: `${msg.charAt(0).toUpperCase() + msg.slice(1)} cost for ${label} should not exceed the quoted amount`,
      field: `${label.toLowerCase()}Cost`,
    };
  }

  if (status === 'Pending') {
    return {
      isValid: false,
      message: `${label} status must be "Confirmed" to proceed.`,
      field: `${label.toLowerCase()}Status`,
    };
  }

  if (agency === '-1') {
    return {
      isValid: false,
      message: `Please select ${label} Travel Agency.`,
      field: `${label.toLowerCase()}Agency`,
    };
  }

  if (!comments?.trim()) {
    return {
      isValid: false,
      message: `Please enter ${label} Booking Reference.`,
      field: `${label.toLowerCase()}Comments`,
    };
  }

  return { isValid: true };
};

export const checkOverlimitBooking = (
  flightRebookCost,
  hotelRebookCost,
  visaRebookCost,
  insuranceRebookCost,
  airportTransferRebookCost,
  flightQuoteCost,
  hotelQuoteCost,
  visaQuoteCost,
  insuranceQuoteCost,
  airportTransferQuoteCost,
  approveStatus,
) => {
  if (
    flightRebookCost - flightQuoteCost > 500 ||
    hotelRebookCost - hotelQuoteCost > 500 ||
    visaRebookCost - visaQuoteCost > 500 ||
    insuranceRebookCost - insuranceQuoteCost > 500 ||
    airportTransferRebookCost - airportTransferQuoteCost > 500
  ) {
    if (approveStatus === 'H') {
      return true;
    }
  }
  return false;
};

// Confirm bookings payload builder, confirm booking validation
export const validateConfirmBooking = (headerDetails, bookingStatus) => {
  const trim = v => (typeof v === 'string' ? v.trim() : v);
  const isY = v => trim(v) === 'Y';
  const isConfirmed = v => trim(v) === 'Confirmed';
  const isMissing = v => !trim(v); // empty comments
  const isNotSelected = v => trim(v) === '-1';

  // Map each segment explicitly so we don't rely on fragile names
  const rules = [
    {
      label: 'Flight',
      flag: headerDetails?.Flight, // e.g., 'Y' / 'N'
      status: bookingStatus?.flightStatus,
      comments: bookingStatus?.flightComments,
      // no agency requirement for Flight
    },
    {
      label: 'Hotel',
      flag: headerDetails?.Hotel,
      status: bookingStatus?.hotelStatus,
      comments: bookingStatus?.hotelComments,
    },
    {
      label: 'Visa',
      flag: headerDetails?.Visa, // old code used IsVisa; adjust if yours is IsVisa
      status: bookingStatus?.visaStatus,
      comments: bookingStatus?.visaComments,
      agencySelected: bookingStatus?.visaTravelAgency, // matches your payload
      agencyMessage: 'Visa Travel Agency',
    },
    {
      label: 'Travel Insurance',
      flag: headerDetails?.TravelInsurance, // adjust if your key is IsInsurance
      status: bookingStatus?.travelInsuranceStatus,
      comments: bookingStatus?.travelInsuranceComments,
      agencySelected: bookingStatus?.travelInsuranceTravelAgency,
      agencyMessage: 'Travel Insurance Agency',
    },
    {
      label: 'Airport Transfer',
      flag: headerDetails?.AirportTransfer, // adjust if your key is IsAirTrans
      status: bookingStatus?.airportTransferStatus,
      comments: bookingStatus?.airportTransferComments,
      agencySelected: bookingStatus?.airportTransferTravelAgency,
      agencyMessage: 'Air Transfer Travel Agency',
    },
  ];

  const hasError = rules.some(r => {
    if (!isY(r.flag)) {
      return false; // skip sections that are not enabled (Y)
    }

    // 1) Status must be Confirmed
    if (!isConfirmed(r.status)) {
      toast.error(`${r.label} status must be "Confirmed" to proceed.`, {
        className: 'toast-error',
      });
      return true;
    }

    // 2) Agency required when the section is Y (this mirrors the legacy checks)
    if (r.agencySelected !== undefined && isNotSelected(r.agencySelected)) {
      toast.error(`Please select ${r.agencyMessage}.`, {
        className: 'toast-error',
      });
      return true;
    }

    // 3) Booking reference/comments required when confirmed
    if (isMissing(r.comments)) {
      toast.error(`Please enter ${r.label} Booking Reference.`, {
        className: 'toast-error',
      });
      return true;
    }

    return false;
  });

  if (hasError) {
    return false;
  }

  return true;
};

// Cost exceed check
export const isConfirmBookingOverLimit = (
  status,
  bookingStatus,
  quotes = {},
) => {
  const getDelta = (cost = 0, quote = 0) =>
    (parseFloat(cost) || 0) - (parseFloat(quote) || 0);

  if (status === 'Pending for Booking') {
    return (
      getDelta(bookingStatus?.flightCost, quotes.flightQuote) > 500 ||
      getDelta(bookingStatus?.hotelCost, quotes.hotelQuote) > 500 ||
      getDelta(bookingStatus?.visaCost, quotes.visaQuote) > 500 ||
      getDelta(bookingStatus?.travelInsuranceCost, quotes.insQuote) > 500 ||
      getDelta(bookingStatus?.airportTransferCost, quotes.airQuote) > 500
    );
  }

  if (status === 'Request for Rebooking') {
    return (
      getDelta(bookingStatus?.flightRebookCost, quotes.flightQuote) > 500 ||
      getDelta(bookingStatus?.hotelRebookCost, quotes.hotelQuote) > 500 ||
      getDelta(bookingStatus?.visaRebookCost, quotes.visaQuote) > 500 ||
      getDelta(bookingStatus?.travelInsuranceRebookCost, quotes.insQuote) >
        500 ||
      getDelta(bookingStatus?.airportTransferRebookCost, quotes.airQuote) > 500
    );
  }
};

// Build confirm booking payload
export const buildBookingPayload = ({
  id,
  r,
  bookingStatus,
  bookingComments,
  status,
}) => {
  const payload = {
    refNo: String(id),
    reqNo: String(id),
    revisionNo: parseInt(r, 10),

    flightCost: parseFloat(bookingStatus?.flightCost || 0),
    flightStatus: String(bookingStatus?.flightStatus || ''),
    flightComments: String(bookingStatus?.flightComments || ''),

    hotelCost: parseFloat(bookingStatus?.hotelCost || 0),
    hotelStatus: String(bookingStatus?.hotelStatus || ''),
    hotelComments: String(bookingStatus?.hotelComments || ''),

    visaCost: parseFloat(bookingStatus?.visaCost || 0),
    visaStatus: String(bookingStatus?.visaStatus || ''),
    visaComments: String(bookingStatus?.visaComments || ''),
    travelAgencyVisa: String(bookingStatus?.visaTravelAgency || ''),

    insuranceCost: parseFloat(bookingStatus?.travelInsuranceCost || 0),
    insuranceStatus: String(bookingStatus?.travelInsuranceStatus || ''),
    insuranceComments: String(bookingStatus?.travelInsuranceComments || ''),
    travelAgencyInsurance: String(
      bookingStatus?.travelInsuranceTravelAgency || '',
    ),

    airportTransferCost: parseFloat(bookingStatus?.airportTransferCost || 0),
    airportTransferStatus: String(bookingStatus?.airportTransferStatus || ''),
    airportTransferComments: String(
      bookingStatus?.airportTransferComments || '',
    ),
    travelAgencyAirTransfer: String(
      bookingStatus?.airportTransferTravelAgency || '',
    ),
  };

  if (status === 'Pending for Booking') {
    payload.bookingComments = bookingComments;
  }

  return payload;
};

// Build rebook payload
export const buildRebookingPayload = ({ id, r, bookingStatus }) => ({
  refNo: String(id),
  revisionNo: parseInt(r, 10),

  flightRebookCost: parseFloat(bookingStatus?.flightRebookCost || 0),
  flightStatus: String(bookingStatus?.flightStatus || ''),
  flightComments: String(bookingStatus?.flightComments || ''),

  hotelRebookCost: parseFloat(bookingStatus?.hotelRebookCost || 0),
  hotelStatus: String(bookingStatus?.hotelStatus || ''),
  hotelComments: String(bookingStatus?.hotelComments || ''),

  visaRebookCost: parseFloat(bookingStatus?.visaRebookCost || 0),
  visaStatus: String(bookingStatus?.visaStatus || ''),
  visaComments: String(bookingStatus?.visaComments || ''),
  travelAgencyVisa: String(bookingStatus?.visaTravelAgency || ''),

  insuranceRebookCost: parseFloat(
    bookingStatus?.travelInsuranceRebookCost || 0,
  ),
  insuranceStatus: String(bookingStatus?.travelInsuranceStatus || ''),
  insuranceComments: String(bookingStatus?.travelInsuranceComments || ''),
  travelAgencyTrvlIns: String(bookingStatus?.travelInsuranceTravelAgency || ''),

  airportTransferRebookCost: parseFloat(
    bookingStatus?.airportTransferRebookCost || 0,
  ),
  airportTransferStatus: String(bookingStatus?.airportTransferStatus || ''),
  airportTransferComments: String(bookingStatus?.airportTransferComments || ''),
  travelAgencyAirTrans: String(
    bookingStatus?.airportTransferTravelAgency || '',
  ),
});

// Build cancel booking payload
export const buildCancelBookingPayload = ({ id, r, bookingStatus }) => ({
  reqNo: String(id),
  revisionNo: parseInt(r, 10),

  flightCancelRefund: parseFloat(bookingStatus?.flightCancelRefund || 0),
  flightStatus: String(bookingStatus?.flightStatus || ''),
  flightComments: String(getTrimmedString(bookingStatus?.flightComments) || ''),

  hotelCancelRefund: parseFloat(bookingStatus?.hotelCancelRefund || 0),
  hotelStatus: String(bookingStatus?.hotelStatus || ''),
  hotelComments: String(getTrimmedString(bookingStatus?.hotelComments) || ''),

  visaCancelRefund: parseFloat(bookingStatus?.visaCancelRefund || 0),
  visaStatus: String(bookingStatus?.visaStatus || ''),
  visaComments: String(getTrimmedString(bookingStatus?.visaComments) || ''),

  insuranceCancelRefund: parseFloat(
    bookingStatus?.travelCancelInsuranceRefund || 0,
  ),
  insuranceStatus: String(bookingStatus?.travelInsuranceStatus || ''),
  insuranceComments: String(
    getTrimmedString(bookingStatus?.travelInsuranceComments) || '',
  ),

  airportTransferCancelRefund: parseFloat(
    bookingStatus?.airportCancelTransferRefund || 0,
  ),
  airportTransferStatus: String(bookingStatus?.airportTransferStatus || ''),
  airportTransferComments: String(
    getTrimmedString(bookingStatus?.airportTransferComments) || '',
  ),
});
